import React from "react";
import { Search, Lightbulb, Code2, Link as LinkIcon, TrendingUp, ArrowRight } from "lucide-react";

const STEPS = [
  {
    title: "Understand",
    desc: "Your business, your goals, your opportunities.",
    icon: Search,
    color: "#0797E1",
  },
  {
    title: "Design",
    desc: "Tailored solutions for real-world impact.",
    icon: Lightbulb,
    color: "#6623B0",
  },
  {
    title: "Build",
    desc: "With the right AI, tech and people.",
    icon: Code2,
    color: "#BE7B2F",
  },
  {
    title: "Integrate",
    desc: "Seamlessly into your existing systems.",
    icon: LinkIcon,
    color: "#139287",
  },
  {
    title: "Scale",
    desc: "Measure, learn, and evolve continuously.",
    icon: TrendingUp,
    color: "#0485C9",
  },
];

export const OurSolutionsSection = () => {
  return (
    <section className="relative w-full overflow-hidden bg-[#020B14] py-20 lg:py-28 text-white">
      {/* Background Image provided by user */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat pointer-events-none opacity-85"
        style={{ backgroundImage: `url('/images/our-solutions-bg.png')` }}
      />
      
      {/* Subtle darkening gradient overlay for top and bottom blending */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#020B14]/40 via-transparent to-[#020B14]/60 pointer-events-none" />

      <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-[70px]">
        {/* Top Header Row */}
        <div className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
          {/* Left: Pre-title & Headline */}
          <div className="flex flex-col gap-2.5">
            <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#94A3B8]">
              OUR SOLUTIONS
            </p>
            <h2 className="text-[28px] sm:text-[34px] lg:text-[36px] xl:text-[40px] font-bold leading-[1.18] tracking-tight text-white lg:whitespace-nowrap">
              How we turn AI into business value
            </h2>
          </div>

          {/* Right: Divider & Paragraph */}
          <div className="flex items-center border-t border-white/15 pt-6 lg:border-t-0 lg:border-l lg:border-white/25 lg:pl-8 lg:pt-0 max-w-[480px]">
            <p className="text-[14px] sm:text-[14.5px] leading-relaxed text-[#CBD5E1]">
              We don't just implement AI. We design and build end-to-end solutions that address your unique business goals, industry context and operational realities — and deliver measurable impact.
            </p>
          </div>
        </div>

        {/* 5-Step Process Workflow */}
        <div className="mt-16 sm:mt-20 flex flex-wrap lg:flex-nowrap items-start justify-between gap-y-10 gap-x-2">
          {STEPS.map((step, idx) => (
            <React.Fragment key={step.title}>
              {/* Step Card */}
              <div className="group flex flex-1 min-w-[140px] max-w-[180px] flex-col items-center text-center mx-auto">
                {/* Step Circle with Icon */}
                <div 
                  className="flex h-14 w-14 items-center justify-center rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.35)] transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: step.color }}
                >
                  <step.icon className="h-6 w-6 text-white" strokeWidth={2.2} />
                </div>

                {/* Step Title */}
                <h3 className="mt-4 text-[17px] font-bold text-white tracking-tight">
                  {step.title}
                </h3>

                {/* Step Description */}
                <p className="mt-1.5 text-[12.5px] leading-[18px] text-[#94A3B8]">
                  {step.desc}
                </p>
              </div>

              {/* Connecting Arrow between steps (hidden on mobile, visible on desktop) */}
              {idx < STEPS.length - 1 && (
                <div className="hidden lg:flex h-14 items-center justify-center px-1 text-white/40">
                  <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OurSolutionsSection;
